import React, { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { APPLAB_ACCENT } from '../../../constants';

const ADHERENCE_OPTIONS = [
  { id: 'off',     label: 'Ignorera',  desc: 'Använd presets' },
  { id: 'guided',  label: 'Inspirerad', desc: 'Löst baserat på varumärket' },
  { id: 'strict',  label: 'Strikt',    desc: 'Följ färger noga' },
];

export default function BrandingSection({ draft, setDraft }) {
  const [url, setUrl]       = useState('');
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState(null);
  const [open, setOpen]     = useState(false);

  const branding = draft.branding || {};
  const colors   = branding.selected_colors || [];

  function setBranding(key, val) {
    setDraft(d => ({ ...d, branding: { ...d.branding, [key]: val } }));
  }

  async function extract() {
    const u = url.trim();
    if (!u) return;
    setLoad(true);
    setError(null);
    try {
      const res = await fetch('/api/extract-branding-from-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: u }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      if (data.colors?.length) {
        setBranding('selected_colors', data.colors.slice(0, 6));
        setBranding('customer_branding_ref', url.trim());
        if (!branding.adherence || branding.adherence === 'off') {
          setBranding('adherence', 'guided');
        }
      } else {
        setError('Inga färger hittades på den URL:en.');
      }
    } catch (e) {
      setError('Kunde inte extrahera branding. Kontrollera URL:en och försök igen.');
    } finally {
      setLoad(false);
    }
  }

  return (
    <Box>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mb: open ? 2 : 0 }}
      >
        <LanguageIcon sx={{ fontSize: 16, color: '#555' }} />
        <Typography sx={{ color: '#888', fontSize: '0.82rem' }}>
          Kundbranding (valfritt)
        </Typography>
        <Typography sx={{ color: '#444', fontSize: '0.75rem', ml: 'auto' }}>
          {open ? '▲' : '▼'}
        </Typography>
      </Box>

      {open && (
        <Box sx={{ pl: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* URL extraction */}
          <Box>
            <Typography sx={{ color: '#666', fontSize: '0.78rem', mb: 1 }}>
              Hämta färger från kundens hemsida
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth size="small"
                value={url}
                onChange={e => setUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); extract(); } }}
                placeholder="https://kund.se"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white', bgcolor: 'rgba(255,255,255,0.04)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                    '&.Mui-focused fieldset': { borderColor: APPLAB_ACCENT },
                  },
                  '& input::placeholder': { color: '#555', opacity: 1 },
                }}
              />
              <Button
                onClick={extract}
                disabled={loading || !url.trim()}
                variant="outlined"
                sx={{
                  borderColor: 'rgba(255,255,255,0.15)', color: '#aaa', textTransform: 'none',
                  fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap', px: 2,
                  '&:hover': { borderColor: APPLAB_ACCENT, color: APPLAB_ACCENT },
                  '&:disabled': { borderColor: 'rgba(255,255,255,0.06)', color: '#444' },
                }}
              >
                {loading ? <CircularProgress size={14} sx={{ color: '#888' }} /> : 'Extrahera'}
              </Button>
            </Box>
            {error && (
              <Typography sx={{ color: '#ef5350', fontSize: '0.73rem', mt: 0.75 }}>{error}</Typography>
            )}
          </Box>

          {/* Detected colors */}
          {colors.length > 0 && (
            <Box>
              <Typography sx={{ color: '#666', fontSize: '0.78rem', mb: 1 }}>Hittade färger</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {colors.map((c, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 32, height: 32, borderRadius: 1, bgcolor: c,
                      border: '2px solid rgba(255,255,255,0.15)', cursor: 'pointer',
                      '&:hover': { borderColor: 'white' },
                    }}
                    title={c}
                  />
                ))}
                <Typography sx={{ color: '#555', fontSize: '0.72rem', ml: 0.5 }}>
                  ({colors.length} färger)
                </Typography>
              </Box>
            </Box>
          )}

          {/* Adherence */}
          {colors.length > 0 && (
            <Box>
              <Typography sx={{ color: '#666', fontSize: '0.78rem', mb: 1 }}>Hur noga ska AI:n följa varumärket?</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {ADHERENCE_OPTIONS.map(opt => {
                  const active = (branding.adherence || 'guided') === opt.id;
                  return (
                    <Box
                      key={opt.id}
                      onClick={() => setBranding('adherence', opt.id)}
                      sx={{
                        flex: 1, p: 1.5, borderRadius: 1.5, cursor: 'pointer', textAlign: 'center',
                        border: `1px solid ${active ? APPLAB_ACCENT + '88' : 'rgba(255,255,255,0.08)'}`,
                        bgcolor: active ? APPLAB_ACCENT + '11' : 'transparent',
                        transition: 'all 0.12s',
                        '&:hover': { borderColor: APPLAB_ACCENT + '55' },
                      }}
                    >
                      <Typography fontWeight={700} sx={{ color: active ? APPLAB_ACCENT : '#888', fontSize: '0.8rem' }}>{opt.label}</Typography>
                      <Typography sx={{ color: '#555', fontSize: '0.7rem' }}>{opt.desc}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {colors.length === 0 && (
            <Typography sx={{ color: '#444', fontSize: '0.73rem' }}>
              Ange en URL ovan för att automatiskt hämta kundvärden, eller lämna tomt för att använda presets.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
